import { FirebaseApp } from 'firebase/app';
import {
  AppCheckToken,
  CustomProvider,
  ReCaptchaV3Provider,
} from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import {
  BreakerStorage,
  RecaptchaCircuitBreaker,
  toErrorCode,
  withTimeout,
} from './app-check-fallback.utils';

/**
 * App-Check-Provider mit serverseitigem Rückfallweg.
 *
 * Primärpfad bleibt reCAPTCHA v3 — gleicher Bot-Schutz, gleiche Kosten wie
 * bisher. Nur wenn reCAPTCHA kein Token liefert (blockiertes Script, fehlender
 * `_GRECAPTCHA`-Cookie, Timeout bei schlechtem Empfang), holt der Provider für
 * **angemeldete** Nutzer ein Token bei der Callable `issueAppCheckToken`.
 *
 * Ohne diesen Fallback weist Firestore (PROD `ENFORCED`) jede Operation ab und
 * die App ist für die Betroffenen unbenutzbar — bei völlig intaktem Konto.
 * Siehe `docs/architecture/APP_CHECK_ENFORCEMENT.md`.
 *
 * Der Fallback ist kein Freifahrtschein: Ohne Anmeldung gibt es kein Token,
 * und die Function verlangt ein gültiges Firebase-ID-Token.
 */

/**
 * Die internen Provider-Methoden sind im öffentlichen Typ von
 * `@firebase/app-check` ausgeblendet (`/* Excluded from this release type *​/`),
 * existieren zur Laufzeit aber seit v9 unverändert. Dieses Interface bündelt
 * den nötigen Cast an genau einer Stelle.
 */
interface InternalAppCheckProvider {
  initialize(app: FirebaseApp): void;
  getToken(): Promise<AppCheckToken>;
}

export interface AppCheckFallbackOptions {
  /** reCAPTCHA-v3-Site-Key — Primärpfad. */
  recaptchaSiteKey: string;
  /** Firebase-Web-App-ID, für die das Fallback-Token ausgestellt wird. */
  appId: string;
  /** Firebase-Projekt-ID, zum Bilden der Callable-URL. */
  projectId: string;
  /** Region der Cloud Functions. */
  region?: string;
  /**
   * Frist für den reCAPTCHA-Versuch. reCAPTCHA kennt selbst keinen Timeout:
   * Kommt das Script nicht durch, hängt der Aufruf, bis das Netz aufgibt.
   */
  recaptchaTimeoutMs?: number;
  /** Frist, innerhalb derer ein Auth-Zustand vorliegen muss. */
  authTimeoutMs?: number;
  /** Sperrzeit für den reCAPTCHA-Pfad nach einem Fehlschlag. */
  circuitOpenMs?: number;
}

const DEFAULT_REGION = 'europe-west3';
const DEFAULT_RECAPTCHA_TIMEOUT_MS = 8_000;
const DEFAULT_AUTH_TIMEOUT_MS = 5_000;
const DEFAULT_CIRCUIT_OPEN_MS = 10 * 60 * 1000;

interface IssueTokenResponse {
  result?: {
    success?: boolean;
    token?: string;
    expireTimeMillis?: number;
  };
}

/**
 * Stellt sicher, dass die internen Provider-Methoden zur Laufzeit wirklich
 * existieren.
 *
 * Der Aufrufer in `firebase.config.ts` fängt einen Fehler aus dem Konstruktor
 * und startet dann auf dem reinen reCAPTCHA-Pfad. Ohne diese Prüfung liefe ein
 * SDK-Update, das `initialize`/`getToken` umbenennt, erst in `initialize()`
 * auf die Nase — also mitten in `initializeAppCheck`, wo derselbe Fehler die
 * gesamte App-Initialisierung abbricht. Die Prüfung zieht den Fehlschlag an
 * die Stelle vor, an der er noch aufgefangen werden kann.
 */
function assertInternalProviderApi(recaptcha: ReCaptchaV3Provider): void {
  const base =
    CustomProvider.prototype as unknown as Partial<InternalAppCheckProvider>;
  const primary = recaptcha as unknown as Partial<InternalAppCheckProvider>;

  if (typeof base.initialize !== 'function') {
    throw new Error(
      'App Check: CustomProvider.prototype.initialize fehlt — SDK-Änderung'
    );
  }

  if (
    typeof primary.initialize !== 'function' ||
    typeof primary.getToken !== 'function'
  ) {
    throw new Error(
      'App Check: ReCaptchaV3Provider ohne initialize/getToken — SDK-Änderung'
    );
  }
}

/**
 * Löst `sessionStorage` defensiv auf.
 *
 * Genau in den Browsern, um die es hier geht, ist er heikel: Bei blockierten
 * Cookies wirft schon der Zugriff auf die Eigenschaft, im Safari-Privatmodus
 * historisch erst das Schreiben. Deshalb einmal zur Probe schreiben. Ohne
 * Storage arbeitet der Breaker rein im Speicher — wie bisher.
 */
function resolveBreakerStorage(): BreakerStorage | null {
  try {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }

    const probeKey = 'awo-care.appcheck.probe';
    sessionStorage.setItem(probeKey, '1');
    sessionStorage.removeItem(probeKey);

    return sessionStorage;
  } catch {
    return null;
  }
}

export class AppCheckFallbackProvider extends CustomProvider {
  private app?: FirebaseApp;
  private readonly recaptcha: ReCaptchaV3Provider;
  private readonly breaker: RecaptchaCircuitBreaker;
  private readonly options: Required<AppCheckFallbackOptions>;

  constructor(options: AppCheckFallbackOptions) {
    // CustomProvider erledigt für uns das Auslesen des `iat`-Claims aus dem
    // JWT und ergänzt `issuedAtTimeMillis`, das der App-Check-Kern erwartet.
    super({ getToken: () => this.resolveToken() });

    this.options = {
      region: DEFAULT_REGION,
      recaptchaTimeoutMs: DEFAULT_RECAPTCHA_TIMEOUT_MS,
      authTimeoutMs: DEFAULT_AUTH_TIMEOUT_MS,
      circuitOpenMs: DEFAULT_CIRCUIT_OPEN_MS,
      ...options,
    };

    this.recaptcha = new ReCaptchaV3Provider(this.options.recaptchaSiteKey);
    assertInternalProviderApi(this.recaptcha);

    this.breaker = new RecaptchaCircuitBreaker(
      this.options.circuitOpenMs,
      undefined,
      resolveBreakerStorage()
    );
  }

  /**
   * Wird vom App-Check-Kern aufgerufen. Reicht die App an beide Pfade weiter —
   * der reCAPTCHA-Provider braucht sie, um überhaupt initialisieren zu können.
   *
   * Kein `override`: Die Basismethode existiert zur Laufzeit, ist im
   * öffentlichen Typ von `CustomProvider` aber ausgeblendet. Sie wird deshalb
   * weiter unten explizit über das Prototype aufgerufen. Dass sie da ist,
   * hat {@link assertInternalProviderApi} bereits im Konstruktor geprüft.
   */
  initialize(app: FirebaseApp): void {
    this.app = app;
    (
      CustomProvider.prototype as unknown as InternalAppCheckProvider
    ).initialize.call(this, app);
    (this.recaptcha as unknown as InternalAppCheckProvider).initialize(app);
  }

  private async resolveToken(): Promise<AppCheckToken> {
    if (!this.breaker.isOpen()) {
      try {
        const token = await withTimeout(
          (this.recaptcha as unknown as InternalAppCheckProvider).getToken(),
          this.options.recaptchaTimeoutMs,
          'reCAPTCHA'
        );
        this.breaker.recordSuccess();
        return token;
      } catch (error) {
        const code = toErrorCode(error);
        this.breaker.recordFailure();
        console.warn(
          `App Check: reCAPTCHA lieferte kein Token (${code}) — versuche Server-Fallback`
        );
        return this.requestServerToken(code);
      }
    }

    return this.requestServerToken('recaptcha-circuit-open');
  }

  /**
   * Holt ein App-Check-Token bei der Callable. Bewusst per `fetch` statt über
   * das Functions-SDK: Dieses hängt an jeden Aufruf automatisch ein
   * App-Check-Token an und würde damit rekursiv wieder hier landen.
   */
  private async requestServerToken(reason: string): Promise<AppCheckToken> {
    if (!this.app) {
      throw new Error('App Check: Provider wurde nicht initialisiert');
    }

    const idToken = await this.getIdToken();

    if (!idToken) {
      // Kein angemeldeter Nutzer: Der Fallback greift bewusst nicht. Der Fehler
      // wandert zum App-Check-Kern, der ihn an den aufrufenden Dienst
      // durchreicht (z.B. als Firestore `permission-denied`).
      throw new Error(
        'App Check: reCAPTCHA fehlgeschlagen und kein angemeldeter Nutzer für den Fallback'
      );
    }

    const url = `https://${this.options.region}-${this.options.projectId}.cloudfunctions.net/issueAppCheckToken`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        data: { appId: this.options.appId, reason },
      }),
    });

    if (!response.ok) {
      throw new Error(
        `App Check: Fallback-Token fehlgeschlagen (HTTP ${response.status})`
      );
    }

    const payload = (await response.json()) as IssueTokenResponse;
    const token = payload.result?.token;
    const expireTimeMillis = payload.result?.expireTimeMillis;

    if (!token || typeof expireTimeMillis !== 'number') {
      throw new Error('App Check: Fallback-Token war unvollständig');
    }

    console.info('App Check: Server-Fallback-Token bezogen');

    return { token, expireTimeMillis };
  }

  /**
   * Wartet begrenzt auf den Auth-Zustand. Beim Kaltstart fragt der
   * App-Check-Kern oft schon ein Token an, bevor Firebase Auth die Session aus
   * dem IndexedDB-Speicher wiederhergestellt hat — ohne das Warten würde der
   * Fallback dann fälschlich als „nicht angemeldet" abbrechen.
   */
  private async getIdToken(): Promise<string | null> {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const auth = getAuth(this.app!);

    try {
      await withTimeout(
        auth.authStateReady(),
        this.options.authTimeoutMs,
        'Auth'
      );
    } catch {
      return null;
    }

    if (!auth.currentUser) {
      return null;
    }

    try {
      return await auth.currentUser.getIdToken();
    } catch {
      return null;
    }
  }
}
